import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../Components/Header";
import { Crown, Check, Lock, ExternalLink, ArrowLeft } from 'lucide-react';
import SubscriptionPopup from "../Components/SubscriptionPopup";
import { useUser } from '../context/userContext';
import { usePremiumCheck } from './hooks/usePremiumCheck';
import { useAuthContext } from "../context/AuthContext";

const Club = () => {
  const navigate = useNavigate();
  const [selectedSection, setSelectedSection] = useState("");
  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);
  
  const { 
    id: userId,
    loading: userLoading,
    userType  // Добавлен тип пользователя
  } = useUser();
  
  const { user: firebaseUser } = useAuthContext();
  
  const { 
    isPremium, 
    loading: premiumLoading,
    subscriptionData
  } = usePremiumCheck(userId, userType);

  const effectivePremium = isPremium;

  const CLUB_CHANNEL_LINK = "https://t.me/+lYjcWmHetyc4YTIy";
  const CLUB_CHAT_LINK = "https://t.me/+w-b6P6tZhDYxMzIy";

  const cards = [
    {
      id: "master",
      title: "БИБЛИОТЕКА МАСТЕР-КЛАССОВ",
      subtitle: "Стартовый набор",
      image: "/club/card1.png",
      path: "/master",
    },
    {
      id: "knowledge",
      title: "БАЗА ЗНАНИЙ",
      subtitle: "Творчество и проявленность",
      image: "/club/card2.png",
      path: "/knowledge-base",
    },
    {
      id: "tehniki",
      title: "ТЕХНИКИ",
      subtitle: "Упражнения, цвет, текстуры",
      image: "/club/card5.png",
      path: "/Tehniki",
    },
    {
      id: "meditations",
      title: "МЕДИТАЦИИ И ПРАКТИКИ",
      subtitle: "Слушать аудиотреки",
      image: "/club/card3.png",
      path: "/meditations",
    },
    {
      id: "paths",
      title: "МИНИ-ПУТИ",
      subtitle: "Короткие творческие путешествия",
      image: "/club/card4.png",
      path: "/miniputi",
    },
    {
      id: "gosti",
      title: "ГОСТИ АКАДЕМИИ",
      subtitle: "Записи эфиров, уроки",
      image: "/club/card6.png",
      path: "/guest",
    },
  ];

  // Telegram WebApp инициализация (только для Telegram пользователей)
  useEffect(() => {
    if (userType === 'telegram' && window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;
      
      tg.ready();
      tg.expand();
      
      tg.BackButton.offClick();
      tg.BackButton.isVisible = false;
      
      setTimeout(() => {
        tg.BackButton.onClick(() => {
          console.log('Back button clicked in Club');
          navigate(-1);
        });
        tg.BackButton.isVisible = true;
      }, 50);
    }

    return () => {
      if (userType === 'telegram' && window.Telegram?.WebApp) {
        const tg = window.Telegram.WebApp;
        tg.BackButton.offClick();
        tg.BackButton.isVisible = false;
      }
    };
  }, [navigate, userType]);

  // Дополнительный эффект для перехвата момента, когда страница становится активной
  useEffect(() => {
    const handleFocus = () => {
      if (userType === 'telegram' && window.Telegram?.WebApp) {
        const tg = window.Telegram.WebApp;
        
        tg.BackButton.offClick();
        tg.BackButton.onClick(() => {
          console.log('Back button clicked in Club (focus)');
          navigate(-1);
        });
        tg.BackButton.isVisible = true;
      }
    };

    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [navigate, userType]);

  const handleCardClick = (card) => {
    if (!effectivePremium) {
      setSelectedSection(card.title);
      setShowSubscriptionPopup(true);
    } else {
      navigate(card.path);
    }
  };

  const openClubChannel = () => {
    if (effectivePremium) {
      window.open(CLUB_CHANNEL_LINK, '_blank', 'noopener,noreferrer');
    }
  };

  const openClubChat = () => {
    if (effectivePremium) {
      window.open(CLUB_CHAT_LINK, '_blank', 'noopener,noreferrer');
    }
  };

  if (!userId || userLoading || premiumLoading) {
    return (
      <>
        <div
          className="fixed inset-0"
          style={{
            backgroundImage: "url('/bghome.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            zIndex: -1,
          }}
        />
        <div className="min-h-screen w-full relative z-10 overflow-y-auto" 
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}>
          <style>{`
            div::-webkit-scrollbar {
              display: none;
            }
          `}</style>
          <Header />
          <div className="flex flex-col justify-center items-center h-64">
            <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mb-4"></div>
            <div className="text-white text-lg">
              {!userId ? 'Авторизация...' : 'Загрузка...'}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div
        className="fixed inset-0"
        style={{
          backgroundImage: "url('/bghome.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          zIndex: -1,
        }}
      />

      <div 
        className="min-h-screen w-full relative z-10 overflow-y-auto"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        <style>{`
          div::-webkit-scrollbar {
            display: none;
          }
        `}</style>
        
        <Header />
        
        <div className="px-4 pt-2">
          <div className="flex justify-between items-center">
            <h1 className="text-white text-[24px] font-semibold">
              КЛУБ
            </h1>
            
            {effectivePremium && (
              <div className="flex items-center bg-gradient-to-r from-purple-600 to-pink-600 px-3 py-1 rounded-full">
                <Crown className="w-4 h-4 text-yellow-300 mr-1" />
                <span className="text-white text-xs font-medium">PRO</span>
              </div>
            )}
          </div>
          
          {effectivePremium && subscriptionData && (
            <div className="mt-2 bg-green-500/20 border border-green-500/30 rounded-lg p-2">
              <div className="flex items-center">
                <Check className="w-4 h-4 text-green-400 mr-2" />
                <span className="text-green-200 text-sm">
                  {subscriptionData.cancelAtPeriodEnd ? 'Подписка отменена' : 'PRO подписка активна'}
                </span>
              </div>
              {subscriptionData?.currentPeriodEnd && (
                <div className="text-green-300 text-xs mt-1">
                  {subscriptionData.cancelAtPeriodEnd 
                    ? `Доступ до ${new Date(subscriptionData.currentPeriodEnd).toLocaleDateString('ru-RU')}`
                    : `Действует до ${new Date(subscriptionData.currentPeriodEnd).toLocaleDateString('ru-RU')}`
                  }
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4 px-4 mt-6">
          {cards.map((card, index) => (
            <button
              key={index}
              onClick={() => handleCardClick(card)}
              className="relative w-full h-[140px] rounded-[20px] overflow-hidden active:scale-[0.98] transition-transform group"
            >
              <div
                className="absolute inset-0 bg-cover bg-left"
                style={{ 
                  backgroundImage: `url(${card.image})`,
                  backgroundPosition: "left center",
                  backgroundSize: "cover",
                  backgroundRepeat: "no-repeat"
                }}
              />

              {!effectivePremium && (
                <div className="absolute inset-0 bg-black/70 z-5" />
              )}

              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/20 z-5" />

              <div className="relative z-10 h-full flex flex-col justify-center items-end text-right pr-3 pl-32">
                <div
                  className="text-[#8D85D5] text-[16px] leading-tight font-medium max-w-[180px]"
                  style={{ textShadow: "0 0 6px rgba(0,0,0,0.6)" }}
                >
                  {card.title}
                </div>
                <div
                  className="text-[#34D3FF] text-[14px] mt-1 max-w-[180px]"
                  style={{ textShadow: "0 0 6px rgba(0,0,0,0.6)" }}
                >
                  {card.subtitle}
                </div>
                
                {!effectivePremium && (
                  <div className="mt-2 px-3 py-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full text-xs text-white flex items-center">
                    <Lock className="w-3 h-3 mr-1" />
                    Требуется PRO
                  </div>
                )}
                
                {effectivePremium && (
                  <div className="mt-2 px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full text-xs text-white flex items-center">
                    <Check className="w-3 h-3 mr-1" />
                    Доступ открыт
                  </div>
                )}
              </div>
              
              <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors z-5" />
            </button>
          ))}
        </div>

        <div className={`mt-10 px-4 ${!effectivePremium ? 'opacity-70' : ''}`}>
          <h2 className="text-white text-[22px] font-semibold mb-4">
            СООБЩЕСТВО КЛУБА
          </h2>

          <div className="flex flex-col gap-4">
            <button 
              onClick={openClubChannel}
              className="h-[64px] rounded-full bg-[#6B46D9] flex items-center justify-center text-white text-[18px] font-medium active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed group relative overflow-hidden"
              disabled={!effectivePremium}
            >
              <span className="flex items-center">
                📣 Канал Клуба
                {effectivePremium && (
                  <ExternalLink className="w-4 h-4 ml-2 opacity-70" />
                )}
              </span>
              
              {effectivePremium && (
                <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors" />
              )}
            </button>

            <button 
              onClick={openClubChat}
              className="h-[64px] rounded-full bg-[#6B46D9] flex items-center justify-center text-white text-[18px] font-medium active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed group relative overflow-hidden"
              disabled={!effectivePremium}
            >
              <span className="flex items-center">
                💬 Чат Клуба
                {effectivePremium && (
                  <ExternalLink className="w-4 h-4 ml-2 opacity-70" />
                )}
              </span>
              
              {effectivePremium && (
                <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors" />
              )}
            </button>
          </div>
          
          {effectivePremium && (
            <div className="text-center mt-3">
              <div className="text-white/80 text-sm">
                Нажмите на кнопку, чтобы перейти в Telegram
              </div>
            </div>
          )}
        </div>
        
        <div className="h-8"></div>
      </div>

      <SubscriptionPopup
        isOpen={showSubscriptionPopup}
        onClose={() => {
          setShowSubscriptionPopup(false);
          setSelectedSection("");
        }}
        subscriptionInfo={{
          active: effectivePremium,
          userId: userId,
          userType: userType,
          stripeCustomerId: subscriptionData?.stripeCustomerId,
          price: subscriptionData?.price || 55,
          currency: subscriptionData?.currency || '€',
          period: 'в месяц',
          features: []
        }}
      />
    </>
  );
};

export default Club;